import { VisionPromptContentImageInterface } from "@dashart/dashart-gpt-shared-library";
import { classNames } from 'primereact/utils';
import { Dialog } from 'primereact/dialog';
import * as React from "react";
import { ChangeEvent, DragEvent, memo, ReactElement, useCallback, useState, useEffect } from 'react';
import './ImageFileSelector.scss';
import ImagePromptUtil from "../../../app/utils/ImagePromptUtil";

interface ImageFileSelectorProps {
    image: VisionPromptContentImageInterface | null;
    className?: string;
    dropZone?: ReactElement; // Custom drop zone element
    width?: number;
    height?: number;
    dataTestId?: string;
    imageIndex?: number; // Current image index (1-based)
    totalImages?: number; // Total number of images
    allImages?: VisionPromptContentImageInterface[]; // All images for navigation
    
    onChange: (currentImageValue: VisionPromptContentImageInterface | null, newImageValue: VisionPromptContentImageInterface | null) => void;
}

// SVG Plus Icon component
const PlusIcon = () => (
    <svg
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
    </svg>
);

const ImageFileSelector: React.FC<ImageFileSelectorProps> = memo(({
    image: currentImage,
    dataTestId,
    onChange,
    className,
    dropZone,
    width,
    height,
    imageIndex,
    totalImages,
    allImages
}) => {
    // State for image preview
    const [imagePreview, setImagePreview] = useState<VisionPromptContentImageInterface | null>(currentImage);

    // Refs for separate file inputs (camera and gallery)
    const cameraInputRef = React.useRef<HTMLInputElement>(null);
    const galleryInputRef = React.useRef<HTMLInputElement>(null);

    // Key for resetting file inputs
    const [inputKey, setInputKey] = useState(0);

    // State for image overlay/fullscreen view
    const [showImageOverlay, setShowImageOverlay] = useState<boolean>(false);
    const [overlayImageIndex, setOverlayImageIndex] = useState<number>(0);

    // State for source selection menu (mobile only)
    const [showSourceMenu, setShowSourceMenu] = useState<boolean>(false);

    /**
     * Reset the image preview when the image prop changes
     */
    React.useEffect(() => {
        setImagePreview(currentImage);
    }, [currentImage]);

    /**
     * Reset file inputs when image becomes null
     */
    React.useEffect(() => {
        if (currentImage === null) {
            setInputKey(prevKey => prevKey + 1);
            if (cameraInputRef.current) {
                cameraInputRef.current.value = '';
            }
            if (galleryInputRef.current) {
                galleryInputRef.current.value = '';
            }
        }
    }, [currentImage]);

    /**
     * Process and validate selected image file, resize if needed
     */
    const handleFileInput = useCallback(
        async (file: File) => {
            if (!file?.type.startsWith('image/')) {
                console.warn('Invalid file type. Please select an image file.');
                return;
            }

            try {
                const imageObject = await ImagePromptUtil.resizeImageFile(
                    file,
                    ImagePromptUtil.IMAGE_CONFIG.MAX_WIDTH,
                    ImagePromptUtil.IMAGE_CONFIG.MAX_HEIGHT
                );
                setImagePreview(imageObject);
                onChange(currentImage, imageObject);
            } catch (error) {
                console.error('Error processing image:', error);
            }
        },
        [onChange]
    );

    /**
     * Handle file input change event
     */
    const onFileSelect = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files?.[0];
            if (file) {
                handleFileInput(file);
            }
        },
        [handleFileInput, onChange]
    );

    /**
     * Handle click on add image button
     * Shows source menu on mobile (iOS/Android), opens file picker directly on desktop
     */
    const onIconClick = useCallback(() => {
        // Check if device is mobile (iOS or Android)
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

        if (isMobile) {
            setShowSourceMenu(true);
        } else {
            // Desktop: use gallery input directly
            galleryInputRef.current?.click();
        }
    }, []);

    /**
     * Handle camera option selected from menu
     */
    const handleCameraClick = useCallback(() => {
        setShowSourceMenu(false);
        cameraInputRef.current?.click();
    }, []);

    /**
     * Handle gallery option selected from menu
     */
    const handleGalleryClick = useCallback(() => {
        setShowSourceMenu(false);
        galleryInputRef.current?.click();
    }, []);

    /**
     * Handle drag and drop file
     */
    const onDrop = useCallback(
        (e: DragEvent<HTMLDivElement>) => {
            e.preventDefault();
            const file = e.dataTransfer.files?.[0];
            if (file) {
                handleFileInput(file);
            }
        },
        [handleFileInput]
    );

    /**
     * Prevent default drag over behavior
     */
    const onDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    }, []);

    /**
     * Remove current image and reset inputs
     */
    const removeImage = useCallback(() => {
        onChange(currentImage, null);

        if (cameraInputRef.current) {
            cameraInputRef.current.value = '';
        }
        if (galleryInputRef.current) {
            galleryInputRef.current.value = '';
        }
    }, [onChange]);

    /**
     * Handle click on image preview to show fullscreen overlay
     */
    const handleImageClick = useCallback(() => {
        if (imageIndex) {
            setOverlayImageIndex(imageIndex - 1); // Convert to 0-based index
        }
        setShowImageOverlay(true);
    }, [imageIndex]);

    /**
     * Navigate to previous image in overlay
     */
    const handleNavigatePrevious = useCallback(() => {
        if (overlayImageIndex > 0) {
            setOverlayImageIndex(overlayImageIndex - 1);
        }
    }, [overlayImageIndex]);

    /**
     * Navigate to next image in overlay
     */
    const handleNavigateNext = useCallback(() => {
        if (allImages && overlayImageIndex < allImages.length - 1) {
            setOverlayImageIndex(overlayImageIndex + 1);
        }
    }, [overlayImageIndex, allImages]);

    /**
     * Handle keyboard navigation in overlay (Arrow keys and Escape)
     */
    useEffect(() => {
        if (!showImageOverlay) return;

        const handleKeyDown = (e: KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowLeft':
                    e.preventDefault();
                    handleNavigatePrevious();
                    break;
                case 'ArrowRight':
                    e.preventDefault();
                    handleNavigateNext();
                    break;
                case 'Escape':
                    e.preventDefault();
                    setShowImageOverlay(false);
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showImageOverlay, handleNavigatePrevious, handleNavigateNext]);

    /**
     * Create wrapped drop zone with drag and drop handlers if custom drop zone is provided
     */
    const wrappedDropZone = dropZone ? (
        <div
            onDrop={onDrop}
            onDragOver={onDragOver}
            style={{ cursor: 'pointer' }}
        >
            {React.cloneElement(dropZone, {
                onClick: onIconClick
            })}
        </div>
    ) : null;

    return (
        <div className={classNames('ImageFileSelector', className)} data-testid={dataTestId}>
            {/* Camera input */}
            <input
                key={`camera-${inputKey}`}
                ref={cameraInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={onFileSelect}
                style={{ display: 'none' }}
            />
            {/* Gallery input */}
            <input
                key={`gallery-${inputKey}`}
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                onChange={onFileSelect}
                style={{ display: 'none' }}
            />

            {!imagePreview ? (
                wrappedDropZone || (
                    <div
                        className="dropzone border-1 border-round d-flex align-items-center justify-content-center"
                        style={{ cursor: 'pointer', width: width, height: height }}
                        onClick={onIconClick}
                        onDrop={onDrop}
                        onDragOver={onDragOver}
                    >
                        <div className="plus-icon-wrapper">
                            <PlusIcon />
                        </div>
                    </div>
                )
            ) : (
                <div className="previews-area d-flex flex-column">
                    <div className="preview-container" style={{ width: width, height: height }}>
                        <img
                            src={imagePreview.imageUrl}
                            alt="Preview"
                            className="preview-image"
                            onClick={handleImageClick}
                        />
                        <div className="remove-icon-wrapper" onClick={removeImage}>
                            <i className="pi pi-times remove-icon" />
                        </div>
                        {imageIndex && totalImages && (
                            <div className="image-counter">
                                {imageIndex}/{totalImages}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Source selection menu */}
            {showSourceMenu && (
                <Dialog
                    visible={true}
                    onHide={() => setShowSourceMenu(false)}
                    className="ImageSourceMenuDialog"
                    draggable={false}
                    header="Choose Image Source"
                    style={{ width: '90vw', maxWidth: '400px' }}
                >
                    <div className="image-source-menu">
                        <button
                            className="source-option p-3 w-full text-left border-1 border-round mb-2"
                            onClick={handleCameraClick}
                        >
                            <i className="pi pi-camera mr-2" />
                            Take Photo
                        </button>
                        <button
                            className="source-option p-3 w-full text-left border-1 border-round"
                            onClick={handleGalleryClick}
                        >
                            <i className="pi pi-images mr-2" />
                            Choose from Gallery
                        </button>
                    </div>
                </Dialog>
            )}

            {showImageOverlay && allImages && allImages.length > 0 && (
                <Dialog
                    visible={true}
                    onHide={() => setShowImageOverlay(false)}
                    className="ImageFileSelectorOverlayDialog"
                    draggable={false}
                    style={{ width: '90vw', height: '90vh' }}
                >
                    <div className="full-image-container">
                        {/* Previous button */}
                        {overlayImageIndex > 0 && (
                            <button
                                className="nav-button nav-button-left"
                                onClick={handleNavigatePrevious}
                                title="Previous image (←)"
                            >
                                <i className="pi pi-chevron-left" />
                            </button>
                        )}

                        {/* Image */}
                        <img
                            src={allImages[overlayImageIndex]?.imageUrl}
                            alt="Full size preview"
                            className="full-image"
                        />

                        {/* Next button */}
                        {overlayImageIndex < allImages.length - 1 && (
                            <button
                                className="nav-button nav-button-right"
                                onClick={handleNavigateNext}
                                title="Next image (→)"
                            >
                                <i className="pi pi-chevron-right" />
                            </button>
                        )}

                        {/* Image counter in overlay */}
                        <div className="overlay-image-counter">
                            {overlayImageIndex + 1} / {allImages.length}
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
});

ImageFileSelector.displayName = 'ImageFileSelector';

export default ImageFileSelector;